import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import {
  useClientInvoices,
  useCreateClientInvoice,
  useDeleteClientInvoice,
  useUpdateClientInvoicePayment,
} from "@/features/finances/hooks/useFinances";
import type { ClientInvoice } from "@/features/finances/types";
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  CLIENT_INVOICE_STATUS_LABELS,
  CLIENT_INVOICE_STATUS_COLORS,
  PAYMENT_TERM_DAY_OPTIONS,
} from "@/features/finances/types";

interface Props {
  projectId: string;
  canEdit: boolean;
}

const emptyForm = {
  invoice_number: "",
  amount_net: "",
  amount_gross: "",
  invoice_date: "",
  payment_terms_days: 30,
  description: "",
};

export function ClientInvoicesPanel({ projectId, canEdit }: Props) {
  const { data: invoices = [], isLoading } = useClientInvoices({ project: projectId });
  const createMutation = useCreateClientInvoice(projectId);
  const deleteMutation = useDeleteClientInvoice(projectId);
  const updatePayment = useUpdateClientInvoicePayment(projectId);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErr, setFormErr] = useState<string | null>(null);

  const [paymentModal, setPaymentModal] = useState<{ invoice: ClientInvoice } | null>(null);
  const [payForm, setPayForm] = useState({ new_payment_amount: "", payment_date: "", payment_comment: "" });

  const handleNetChange = (val: string) => {
    const net = parseFloat(val);
    const gross = isNaN(net) ? "" : (Math.round(net * 1.23 * 100) / 100).toString();
    setForm({ ...form, amount_net: val, amount_gross: gross });
  };

  const handleCreate = async () => {
    if (!form.amount_net || !form.invoice_date) {
      setFormErr("Kwota netto i data wystawienia są wymagane.");
      return;
    }
    try {
      await createMutation.mutateAsync({
        project: projectId,
        invoice_number: form.invoice_number,
        amount_net: form.amount_net,
        amount_gross: form.amount_gross,
        invoice_date: form.invoice_date,
        payment_terms_days: form.payment_terms_days,
        description: form.description,
      });
      setForm(emptyForm);
      setShowForm(false);
      setFormErr(null);
    } catch {
      setFormErr("Błąd zapisu faktury.");
    }
  };

  const handleDelete = (id: string) => {
    if (!confirm("Czy na pewno usunąć tę fakturę PRZ?")) return;
    deleteMutation.mutate(id);
  };

  const openPayment = (inv: ClientInvoice) => {
    setPayForm({ new_payment_amount: "", payment_date: "", payment_comment: "" });
    setPaymentModal({ invoice: inv });
  };

  const handleUpdatePayment = async () => {
    if (!paymentModal) return;
    await updatePayment.mutateAsync({ id: paymentModal.invoice.id, data: payForm });
    setPaymentModal(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800 dark:text-gray-200">Faktury PRZ (przychody)</h3>
        {canEdit && (
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? "Anuluj" : "+ Dodaj fakturę PRZ"}
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] border border-[var(--color-primary)] rounded-lg p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Nr faktury <span className="font-normal text-gray-400">(nadaje księgowy)</span></label>
            <input
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              value={form.invoice_number}
              onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              placeholder="np. FV/2026/001"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data wystawienia *</label>
            <DatePicker
              value={form.invoice_date}
              onChange={(v) => setForm({ ...form, invoice_date: v })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kwota netto (PLN) *</label>
            <input
              type="number"
              step="0.01"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              value={form.amount_net}
              onChange={(e) => handleNetChange(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kwota brutto (PLN) — netto × 1,23</label>
            <input
              type="number"
              step="0.01"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400"
              value={form.amount_gross}
              readOnly
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Termin płatności</label>
            <select
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              value={form.payment_terms_days}
              onChange={(e) => setForm({ ...form, payment_terms_days: parseInt(e.target.value) })}
            >
              {PAYMENT_TERM_DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} dni
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Opis</label>
            <input
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Opcjonalny opis"
            />
          </div>
          {formErr && <p className="col-span-2 text-xs text-red-600">{formErr}</p>}
          <div className="col-span-2 flex gap-2">
            <Button size="sm" loading={createMutation.isPending} onClick={handleCreate}>
              Zapisz
            </Button>
            <Button size="sm" variant="secondary" onClick={() => { setShowForm(false); setFormErr(null); }}>
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak faktur PRZ.</p>
      ) : (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {invoices.map((inv) => (
              <div key={inv.id} className="px-3 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                        {inv.invoice_number || <span className="text-gray-400 italic text-xs">bez numeru</span>}
                      </span>
                      <Badge color={CLIENT_INVOICE_STATUS_COLORS[inv.status]}>
                        {CLIENT_INVOICE_STATUS_LABELS[inv.status]}
                      </Badge>
                      {inv.awaiting_accountant && inv.status === "draft" && <span className="text-xs text-orange-600">⏳</span>}
                      {inv.status === "issued" && !inv.sent_to_investor && (
                        <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">nie wysłano</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                      <span>{formatDate(inv.invoice_date, "")}</span>
                      {inv.payment_due_date && <span>Termin: {formatDate(inv.payment_due_date)}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatAmount(inv.amount_gross)} PLN</span>
                    <Badge color={PAYMENT_STATUS_COLORS[inv.payment_status]}>
                      {PAYMENT_STATUS_LABELS[inv.payment_status]}
                    </Badge>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-3 mt-2">
                    <button onClick={() => openPayment(inv)} className="text-xs text-[var(--color-primary)] hover:underline">Płatność</button>
                    {inv.invoice_managed_file_detail?.url && (
                      <a href={inv.invoice_managed_file_detail.url} target="_blank" rel="noreferrer" className="text-xs text-[var(--color-primary)] hover:underline">Pobierz</a>
                    )}
                    <button onClick={() => handleDelete(inv.id)} className="text-xs text-red-500 hover:underline">Usuń</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[500px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nr faktury</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Wystawiona</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Termin</th>
                  <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Netto</th>
                  <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Brutto</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Płatność</th>
                  {canEdit && <th className="px-3 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{inv.invoice_number || <span className="text-gray-400 italic">bez numeru</span>}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Badge color={CLIENT_INVOICE_STATUS_COLORS[inv.status]}>
                          {CLIENT_INVOICE_STATUS_LABELS[inv.status]}
                        </Badge>
                        {inv.awaiting_accountant && inv.status === "draft" && (
                          <span className="text-xs text-orange-600">⏳ Czeka na ksg.</span>
                        )}
                        {inv.status === "issued" && !inv.sent_to_investor && (
                          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">nie wysłano</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{formatDate(inv.invoice_date, "")}</td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                      {formatDate(inv.payment_due_date)}
                      <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">({inv.payment_terms_days}d)</span>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{formatAmount(inv.amount_net)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">{formatAmount(inv.amount_gross)}</td>
                    <td className="px-3 py-2">
                      {inv.invoice_managed_file_detail?.url ? (
                        <a href={inv.invoice_managed_file_detail.url} target="_blank" rel="noreferrer" className="text-xs text-[var(--color-primary)] hover:underline">
                          Pobierz
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Brak pliku</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge color={PAYMENT_STATUS_COLORS[inv.payment_status]}>
                        {PAYMENT_STATUS_LABELS[inv.payment_status]}
                      </Badge>
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => openPayment(inv)} className="text-xs text-[var(--color-primary)] hover:underline mr-2">Płatność</button>
                        <button onClick={() => handleDelete(inv.id)} className="text-xs text-red-500 hover:underline">Usuń</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {paymentModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Zarejestruj wpływ — {paymentModal.invoice.invoice_number}
            </h3>
            {paymentModal.invoice.paid_amount !== "0.00" && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Dotychczas zapłacono: {formatAmount(paymentModal.invoice.paid_amount)} PLN z {formatAmount(paymentModal.invoice.amount_gross)} PLN brutto
              </p>
            )}
            <div className="flex flex-col gap-3 mt-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kwota nowego wpływu (PLN) *</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={payForm.new_payment_amount}
                  onChange={(e) => setPayForm({ ...payForm, new_payment_amount: e.target.value })}
                  placeholder="0.00"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data wpływu na konto *</label>
                <DatePicker
                  value={payForm.payment_date}
                  onChange={(v) => setPayForm({ ...payForm, payment_date: v })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Komentarz</label>
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={payForm.payment_comment}
                  onChange={(e) => setPayForm({ ...payForm, payment_comment: e.target.value })}
                  placeholder="Opcjonalny komentarz"
                />
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" loading={updatePayment.isPending} onClick={handleUpdatePayment} disabled={!payForm.new_payment_amount || !payForm.payment_date}>
                  Zapisz
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setPaymentModal(null)}>
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
